// slices/serviceCategorySlice.js
import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import ServiceCategoryService from "../services/ServiceCategoryService";

// ==================== HELPER: Error Handler ====================
const handleError = (error) => {
  console.log('Full error object:', JSON.stringify(error, null, 2));
  
  let errorMessage = 'An unexpected error occurred. Please try again.';
  
  if (typeof error === 'string') {
    errorMessage = error;
  } else if (error?.data?.error?.message) {
    errorMessage = error.data.error.message;
  } else if (error?.error && typeof error.error === 'string') {
    errorMessage = error.error;
  } else if (error?.message) {
    errorMessage = error.message;
  } else if (error?.response?.data?.error?.message) {
    errorMessage = error.response.data.error.message;
  } else if (error?.response?.data?.error && typeof error.response.data.error === 'string') {
    errorMessage = error.response.data.error;
  } else if (error?.response?.data?.message) {
    errorMessage = error.response.data.message;
  } else if (error?.response?.data?.data?.error?.message) {
    errorMessage = error.response.data.data.error.message;
  } else if (error?.success === false && error?.error) {
    errorMessage = error.error;
  } else if (error?.data && error?.statusCode) {
    if (error.data.error?.message) {
      errorMessage = error.data.error.message;
    } else if (error.data.message) {
      errorMessage = error.data.message;
    }
  } else if (error?.response) {
    if (error.response.data?.error?.message) {
      errorMessage = error.response.data.error.message;
    } else if (error.response.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response.statusText) {
      errorMessage = error.response.statusText;
    }
  } else if (error?.error) {
    if (typeof error.error === 'string') {
      errorMessage = error.error;
    } else if (error.error.message) {
      errorMessage = error.error.message;
    }
  }
  
  console.log('Extracted error message:', errorMessage);
  return errorMessage;
};

// ==================== ASYNC THUNKS ====================

// -------- PUBLIC ROUTES --------

/**
 * Get all categories with filters
 * GET /api/categories
 */
export const getAllCategories = createAsyncThunk(
  'serviceCategory/getAllCategories',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await ServiceCategoryService.getAllCategories(params);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get category with subcategories
 * GET /api/category/:id
 */
export const getCategoryWithSubcategories = createAsyncThunk(
  'serviceCategory/getCategoryWithSubcategories',
  async (id, { rejectWithValue }) => {
    try {
      const response = await ServiceCategoryService.getCategoryWithSubcategories(id);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get categories for dropdown/select
 * GET /api/categories/select
 */
export const getCategoriesForSelect = createAsyncThunk(
  'serviceCategory/getCategoriesForSelect',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ServiceCategoryService.getCategoriesForSelect();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get subcategories for dropdown/select
 * GET /api/category/:id/subcategories/select
 */
export const getSubcategoriesForSelect = createAsyncThunk(
  'serviceCategory/getSubcategoriesForSelect',
  async (categoryId, { rejectWithValue }) => {
    try {
      const response = await ServiceCategoryService.getSubcategoriesForSelect(categoryId);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get category tree (hierarchical)
 * GET /api/categories/tree
 */
export const getCategoryTree = createAsyncThunk(
  'serviceCategory/getCategoryTree',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ServiceCategoryService.getCategoryTree();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get popular categories (by provider count)
 * GET /api/categories/popular
 */
export const getPopularCategories = createAsyncThunk(
  'serviceCategory/getPopularCategories',
  async (limit = 10, { rejectWithValue }) => {
    try {
      const response = await ServiceCategoryService.getPopularCategories(limit);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get categories by provider count
 * GET /api/categories/by-provider-count
 */
export const getCategoriesByProviderCount = createAsyncThunk(
  'serviceCategory/getCategoriesByProviderCount',
  async ({ minCount = 0, options = {} }, { rejectWithValue }) => {
    try {
      const response = await ServiceCategoryService.getCategoriesByProviderCount(minCount, options);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Search subcategories
 * GET /api/category/:id/subcategories/search
 */
export const searchSubcategories = createAsyncThunk(
  'serviceCategory/searchSubcategories',
  async ({ categoryId, searchTerm }, { rejectWithValue }) => {
    try {
      const response = await ServiceCategoryService.searchSubcategories(categoryId, searchTerm);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// -------- ADMIN ROUTES --------

/**
 * Create new category with validation
 * POST /api/category
 */
export const createCategory = createAsyncThunk(
  'serviceCategory/createCategory',
  async (categoryData, { rejectWithValue }) => {
    try {
      const response = await ServiceCategoryService.createCategory(categoryData);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Update category
 * PUT /api/category/:id
 */
export const updateCategory = createAsyncThunk(
  'serviceCategory/updateCategory',
  async ({ id, updateData }, { rejectWithValue }) => {
    try {
      const response = await ServiceCategoryService.updateCategory(id, updateData);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Delete category
 * DELETE /api/category/:id
 */
export const deleteCategory = createAsyncThunk(
  'serviceCategory/deleteCategory',
  async (id, { rejectWithValue }) => {
    try {
      const response = await ServiceCategoryService.deleteCategory(id);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Toggle category status
 * PATCH /api/category/:id/toggle-status
 */
export const toggleCategoryStatus = createAsyncThunk(
  'serviceCategory/toggleCategoryStatus',
  async (id, { rejectWithValue }) => {
    try {
      const response = await ServiceCategoryService.toggleCategoryStatus(id);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Toggle featured status
 * PATCH /api/category/:id/toggle-featured
 */
export const toggleFeaturedStatus = createAsyncThunk(
  'serviceCategory/toggleFeaturedStatus',
  async (id, { rejectWithValue }) => {
    try {
      const response = await ServiceCategoryService.toggleFeaturedStatus(id);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Add subcategory
 * POST /api/category/:id/subcategories
 */
export const addSubcategory = createAsyncThunk(
  'serviceCategory/addSubcategory',
  async ({ categoryId, subcategoryData }, { rejectWithValue }) => {
    try {
      const response = await ServiceCategoryService.addSubcategory(categoryId, subcategoryData);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Update subcategory
 * PUT /api/category/:categoryId/subcategories/:subcategoryId
 */
export const updateSubcategory = createAsyncThunk(
  'serviceCategory/updateSubcategory',
  async ({ categoryId, subcategoryId, updateData }, { rejectWithValue }) => {
    try {
      const response = await ServiceCategoryService.updateSubcategory(categoryId, subcategoryId, updateData);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Remove subcategory
 * DELETE /api/category/:categoryId/subcategories/:subcategoryId
 */
export const removeSubcategory = createAsyncThunk(
  'serviceCategory/removeSubcategory',
  async ({ categoryId, subcategoryId }, { rejectWithValue }) => {
    try {
      const response = await ServiceCategoryService.removeSubcategory(categoryId, subcategoryId);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Reorder categories
 * POST /api/categories/reorder
 */
export const reorderCategories = createAsyncThunk(
  'serviceCategory/reorderCategories',
  async (categoryOrders, { rejectWithValue }) => {
    try {
      const response = await ServiceCategoryService.reorderCategories(categoryOrders);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Bulk update categories
 * PATCH /api/categories/bulk
 */
export const bulkUpdateCategories = createAsyncThunk(
  'serviceCategory/bulkUpdateCategories',
  async ({ ids, updateData }, { rejectWithValue }) => {
    try {
      const response = await ServiceCategoryService.bulkUpdateCategories(ids, updateData);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Bulk delete categories
 * DELETE /api/categories/bulk
 */
export const bulkDeleteCategories = createAsyncThunk(
  'serviceCategory/bulkDeleteCategories',
  async (ids, { rejectWithValue }) => {
    try {
      const response = await ServiceCategoryService.bulkDeleteCategories(ids);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Validate category name uniqueness
 * POST /api/categories/validate-name
 */
export const validateCategoryName = createAsyncThunk(
  'serviceCategory/validateCategoryName',
  async ({ name, excludeId = null }, { rejectWithValue }) => {
    try {
      const response = await ServiceCategoryService.validateCategoryName(name, excludeId);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// -------- SUPER ADMIN ROUTES --------

/**
 * Get category analytics
 * GET /api/categories/analytics
 */
export const getCategoryAnalytics = createAsyncThunk(
  'serviceCategory/getCategoryAnalytics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ServiceCategoryService.getCategoryAnalytics();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// ==================== SLICE ====================

const initialState = {
  // Category data
  categories: [],
  currentCategory: null,
  categoryTree: [],
  categorySelect: [],
  subcategories: [],
  subcategorySelect: [],
  popularCategories: [],
  
  // Search results
  searchResults: [],
  
  // Statistics
  analytics: null,
  
  // Pagination
  pagination: {
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  },
  
  // Filters
  filters: {},
  
  // Loading states
  loading: false,
  loadingCategory: false,
  loadingTree: false,
  loadingSelect: false,
  loadingPopular: false,
  loadingAnalytics: false,
  submitting: false,
  updating: false,
  
  // Error states
  error: null,
  submitError: null,
  updateError: null,
  
  // Success states
  submitSuccess: false,
  updateSuccess: false,
  
  // UI states
  selectedCategoryId: null,
  selectedSubcategoryId: null,
  isEditing: false,
  showSubcategories: false,
};

const serviceCategorySlice = createSlice({
  name: 'serviceCategory',
  initialState,
  reducers: {
    // Reset states
    resetCategoryState: (state) => {
      state.categories = [];
      state.currentCategory = null;
      state.categoryTree = [];
      state.categorySelect = [];
      state.subcategories = [];
      state.subcategorySelect = [];
      state.popularCategories = [];
      state.searchResults = [];
      state.analytics = null;
      state.pagination = {
        total: 0,
        page: 1,
        limit: 20,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
      };
      state.filters = {};
      state.error = null;
      state.submitError = null;
      state.updateError = null;
      state.submitSuccess = false;
      state.updateSuccess = false;
      state.selectedCategoryId = null;
      state.selectedSubcategoryId = null;
    },
    
    // Clear errors
    clearCategoryErrors: (state) => {
      state.error = null;
      state.submitError = null;
      state.updateError = null;
    },
    
    // Clear success states
    clearCategorySuccess: (state) => {
      state.submitSuccess = false;
      state.updateSuccess = false;
    },
    
    // Set filters
    setCategoryFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    
    // Clear filters
    clearCategoryFilters: (state) => {
      state.filters = {};
    },
    
    // Set pagination
    setCategoryPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    
    // Set selected category
    setSelectedCategory: (state, action) => {
      state.selectedCategoryId = action.payload;
    },
    
    // Set selected subcategory
    setSelectedSubcategory: (state, action) => {
      state.selectedSubcategoryId = action.payload;
    },
    
    // Toggle subcategories view
    toggleSubcategories: (state) => {
      state.showSubcategories = !state.showSubcategories;
    },
    
    // Set editing mode
    setEditingCategory: (state, action) => {
      state.isEditing = action.payload;
    },
    
    // Update category locally (optimistic update)
    updateLocalCategory: (state, action) => {
      const { categoryId, updateData } = action.payload;
      
      // Update in categories list
      const categoryIndex = state.categories.findIndex(c => c._id === categoryId);
      if (categoryIndex !== -1) {
        state.categories[categoryIndex] = { ...state.categories[categoryIndex], ...updateData };
      }
      
      // Update current category
      if (state.currentCategory && state.currentCategory._id === categoryId) {
        state.currentCategory = { ...state.currentCategory, ...updateData };
      }
      
      // Update in category tree
      const treeIndex = state.categoryTree.findIndex(c => c.id === categoryId);
      if (treeIndex !== -1) {
        state.categoryTree[treeIndex] = { ...state.categoryTree[treeIndex], ...updateData };
      }
      
      // Update in category select
      const selectIndex = state.categorySelect.findIndex(c => c.value === categoryId);
      if (selectIndex !== -1) {
        state.categorySelect[selectIndex] = { 
          ...state.categorySelect[selectIndex], 
          label: updateData.displayName || state.categorySelect[selectIndex].label,
          name: updateData.name || state.categorySelect[selectIndex].name
        };
      }
    },
    
    // Add local category
    addLocalCategory: (state, action) => {
      const category = action.payload;
      state.categories.unshift(category);
      state.categorySelect.unshift({
        value: category._id,
        label: category.displayName,
        name: category.name
      });
    },
    
    // Remove local category
    removeLocalCategory: (state, action) => {
      const categoryId = action.payload;
      state.categories = state.categories.filter(c => c._id !== categoryId);
      state.categorySelect = state.categorySelect.filter(c => c.value !== categoryId);
      if (state.currentCategory && state.currentCategory._id === categoryId) {
        state.currentCategory = null;
      }
    },
    
    // Update subcategory locally
    updateLocalSubcategory: (state, action) => {
      const { categoryId, subcategoryId, updateData } = action.payload;
      
      // Find category and update subcategory
      const category = state.categories.find(c => c._id === categoryId);
      if (category) {
        const subIndex = category.subcategories.findIndex(s => s._id === subcategoryId);
        if (subIndex !== -1) {
          category.subcategories[subIndex] = { ...category.subcategories[subIndex], ...updateData };
        }
      }
      
      // Update current category
      if (state.currentCategory && state.currentCategory._id === categoryId) {
        const subIndex = state.currentCategory.subcategories.findIndex(s => s._id === subcategoryId);
        if (subIndex !== -1) {
          state.currentCategory.subcategories[subIndex] = { 
            ...state.currentCategory.subcategories[subIndex], 
            ...updateData 
          };
        }
      }
    },
    
    // Add local subcategory
    addLocalSubcategory: (state, action) => {
      const { categoryId, subcategory } = action.payload;
      
      // Add to categories list
      const category = state.categories.find(c => c._id === categoryId);
      if (category) {
        if (!category.subcategories) category.subcategories = [];
        category.subcategories.push(subcategory);
      }
      
      // Add to current category
      if (state.currentCategory && state.currentCategory._id === categoryId) {
        if (!state.currentCategory.subcategories) state.currentCategory.subcategories = [];
        state.currentCategory.subcategories.push(subcategory);
      }
    },
    
    // Remove local subcategory
    removeLocalSubcategory: (state, action) => {
      const { categoryId, subcategoryId } = action.payload;
      
      // Remove from categories list
      const category = state.categories.find(c => c._id === categoryId);
      if (category && category.subcategories) {
        category.subcategories = category.subcategories.filter(s => s._id !== subcategoryId);
      }
      
      // Remove from current category
      if (state.currentCategory && state.currentCategory._id === categoryId) {
        if (state.currentCategory.subcategories) {
          state.currentCategory.subcategories = state.currentCategory.subcategories.filter(
            s => s._id !== subcategoryId
          );
        }
      }
    },
    
    // Set categories with formatted data
    setCategories: (state, action) => {
      state.categories = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // ========== GET ALL CATEGORIES ==========
      .addCase(getAllCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload.data.data || [];
        state.pagination = action.payload.data.pagination || state.pagination;
      })
      .addCase(getAllCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get categories';
      })
      
      // ========== GET CATEGORY WITH SUBCATEGORIES ==========
      .addCase(getCategoryWithSubcategories.pending, (state) => {
        state.loadingCategory = true;
        state.error = null;
      })
      .addCase(getCategoryWithSubcategories.fulfilled, (state, action) => {
        state.loadingCategory = false;
        state.currentCategory = action.payload.data;
        state.subcategories = action.payload.data.subcategories || [];
      })
      .addCase(getCategoryWithSubcategories.rejected, (state, action) => {
        state.loadingCategory = false;
        state.error = action.payload || 'Failed to get category';
      })
      
      // ========== GET CATEGORIES FOR SELECT ==========
      .addCase(getCategoriesForSelect.pending, (state) => {
        state.loadingSelect = true;
        state.error = null;
      })
      .addCase(getCategoriesForSelect.fulfilled, (state, action) => {
        state.loadingSelect = false;
        state.categorySelect = action.payload.data || [];
      })
      .addCase(getCategoriesForSelect.rejected, (state, action) => {
        state.loadingSelect = false;
        state.error = action.payload || 'Failed to get categories for select';
      })
      
      // ========== GET SUBCATEGORIES FOR SELECT ==========
      .addCase(getSubcategoriesForSelect.pending, (state) => {
        state.loadingSelect = true;
        state.error = null;
      })
      .addCase(getSubcategoriesForSelect.fulfilled, (state, action) => {
        state.loadingSelect = false;
        state.subcategorySelect = action.payload.data || [];
      })
      .addCase(getSubcategoriesForSelect.rejected, (state, action) => {
        state.loadingSelect = false;
        state.error = action.payload || 'Failed to get subcategories for select';
      })
      
      // ========== GET CATEGORY TREE ==========
      .addCase(getCategoryTree.pending, (state) => {
        state.loadingTree = true;
        state.error = null;
      })
      .addCase(getCategoryTree.fulfilled, (state, action) => {
        state.loadingTree = false;
        state.categoryTree = action.payload.data || [];
      })
      .addCase(getCategoryTree.rejected, (state, action) => {
        state.loadingTree = false;
        state.error = action.payload || 'Failed to get category tree';
      })
      
      // ========== GET POPULAR CATEGORIES ==========
      .addCase(getPopularCategories.pending, (state) => {
        state.loadingPopular = true;
        state.error = null;
      })
      .addCase(getPopularCategories.fulfilled, (state, action) => {
        state.loadingPopular = false;
        state.popularCategories = action.payload.data || [];
      })
      .addCase(getPopularCategories.rejected, (state, action) => {
        state.loadingPopular = false;
        state.error = action.payload || 'Failed to get popular categories';
      })
      
      // ========== GET CATEGORIES BY PROVIDER COUNT ==========
      .addCase(getCategoriesByProviderCount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCategoriesByProviderCount.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload.data.data || [];
        state.pagination = action.payload.data.pagination || state.pagination;
      })
      .addCase(getCategoriesByProviderCount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get categories by provider count';
      })
      
      // ========== SEARCH SUBCATEGORIES ==========
      .addCase(searchSubcategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchSubcategories.fulfilled, (state, action) => {
        state.loading = false;
        state.searchResults = action.payload.data || [];
      })
      .addCase(searchSubcategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to search subcategories';
      })
      
      // ========== CREATE CATEGORY ==========
      .addCase(createCategory.pending, (state) => {
        state.submitting = true;
        state.submitError = null;
        state.submitSuccess = false;
      })
      .addCase(createCategory.fulfilled, (state, action) => {
        state.submitting = false;
        state.submitSuccess = true;
        const category = action.payload.data;
        state.categories.unshift(category);
        state.categorySelect.unshift({
          value: category._id,
          label: category.displayName,
          name: category.name
        });
        state.currentCategory = category;
      })
      .addCase(createCategory.rejected, (state, action) => {
        state.submitting = false;
        state.submitError = action.payload || 'Failed to create category';
        state.submitSuccess = false;
      })
      
      // ========== UPDATE CATEGORY ==========
      .addCase(updateCategory.pending, (state) => {
        state.updating = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(updateCategory.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        const updatedCategory = action.payload.data;
        state.currentCategory = updatedCategory;
        
        const index = state.categories.findIndex(c => c._id === updatedCategory._id);
        if (index !== -1) {
          state.categories[index] = updatedCategory;
        }
        
        const selectIndex = state.categorySelect.findIndex(c => c.value === updatedCategory._id);
        if (selectIndex !== -1) {
          state.categorySelect[selectIndex] = {
            ...state.categorySelect[selectIndex],
            label: updatedCategory.displayName,
            name: updatedCategory.name
          };
        }
      })
      .addCase(updateCategory.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to update category';
        state.updateSuccess = false;
      })
      
      // ========== DELETE CATEGORY ==========
      .addCase(deleteCategory.pending, (state) => {
        state.updating = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        const deletedId = action.meta.arg;
        state.categories = state.categories.filter(c => c._id !== deletedId);
        state.categorySelect = state.categorySelect.filter(c => c.value !== deletedId);
        if (state.currentCategory && state.currentCategory._id === deletedId) {
          state.currentCategory = null;
        }
      })
      .addCase(deleteCategory.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to delete category';
        state.updateSuccess = false;
      })
      
      // ========== TOGGLE CATEGORY STATUS ==========
      .addCase(toggleCategoryStatus.fulfilled, (state, action) => {
        const updatedCategory = action.payload.data;
        const index = state.categories.findIndex(c => c._id === updatedCategory._id);
        if (index !== -1) {
          state.categories[index] = updatedCategory;
        }
        if (state.currentCategory && state.currentCategory._id === updatedCategory._id) {
          state.currentCategory = updatedCategory;
        }
      })
      
      // ========== TOGGLE FEATURED STATUS ==========
      .addCase(toggleFeaturedStatus.fulfilled, (state, action) => {
        const updatedCategory = action.payload.data;
        const index = state.categories.findIndex(c => c._id === updatedCategory._id);
        if (index !== -1) {
          state.categories[index] = updatedCategory;
        }
        if (state.currentCategory && state.currentCategory._id === updatedCategory._id) {
          state.currentCategory = updatedCategory;
        }
      })
      
      // ========== ADD SUBCATEGORY ==========
      .addCase(addSubcategory.fulfilled, (state, action) => {
        const updatedCategory = action.payload.data;
        const index = state.categories.findIndex(c => c._id === updatedCategory._id);
        if (index !== -1) {
          state.categories[index] = updatedCategory;
        }
        if (state.currentCategory && state.currentCategory._id === updatedCategory._id) {
          state.currentCategory = updatedCategory;
          state.subcategories = updatedCategory.subcategories || [];
        }
      })
      
      // ========== UPDATE SUBCATEGORY ==========
      .addCase(updateSubcategory.fulfilled, (state, action) => {
        const updatedCategory = action.payload.data;
        const index = state.categories.findIndex(c => c._id === updatedCategory._id);
        if (index !== -1) {
          state.categories[index] = updatedCategory;
        }
        if (state.currentCategory && state.currentCategory._id === updatedCategory._id) {
          state.currentCategory = updatedCategory;
          state.subcategories = updatedCategory.subcategories || [];
        }
      })
      
      // ========== REMOVE SUBCATEGORY ==========
      .addCase(removeSubcategory.fulfilled, (state, action) => {
        const updatedCategory = action.payload.data;
        const index = state.categories.findIndex(c => c._id === updatedCategory._id);
        if (index !== -1) {
          state.categories[index] = updatedCategory;
        }
        if (state.currentCategory && state.currentCategory._id === updatedCategory._id) {
          state.currentCategory = updatedCategory;
          state.subcategories = updatedCategory.subcategories || [];
        }
      })
      
      // ========== REORDER CATEGORIES ==========
      .addCase(reorderCategories.fulfilled, (state) => {
        // Refresh categories
        state.loading = true;
      })
      
      // ========== BULK UPDATE CATEGORIES ==========
      .addCase(bulkUpdateCategories.pending, (state) => {
        state.updating = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(bulkUpdateCategories.fulfilled, (state) => {
        state.updating = false;
        state.updateSuccess = true;
      })
      .addCase(bulkUpdateCategories.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to bulk update categories';
        state.updateSuccess = false;
      })
      
      // ========== BULK DELETE CATEGORIES ==========
      .addCase(bulkDeleteCategories.pending, (state) => {
        state.updating = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(bulkDeleteCategories.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        const deletedIds = action.meta.arg;
        state.categories = state.categories.filter(c => !deletedIds.includes(c._id));
        state.categorySelect = state.categorySelect.filter(c => !deletedIds.includes(c.value));
        if (state.currentCategory && deletedIds.includes(state.currentCategory._id)) {
          state.currentCategory = null;
        }
      })
      .addCase(bulkDeleteCategories.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to bulk delete categories';
        state.updateSuccess = false;
      })
      
      // ========== VALIDATE CATEGORY NAME ==========
      .addCase(validateCategoryName.fulfilled, (state, action) => {
        // Validation result is used in components
        state.error = null;
      })
      
      // ========== GET CATEGORY ANALYTICS ==========
      .addCase(getCategoryAnalytics.pending, (state) => {
        state.loadingAnalytics = true;
        state.error = null;
      })
      .addCase(getCategoryAnalytics.fulfilled, (state, action) => {
        state.loadingAnalytics = false;
        state.analytics = action.payload.data;
      })
      .addCase(getCategoryAnalytics.rejected, (state, action) => {
        state.loadingAnalytics = false;
        state.error = action.payload || 'Failed to get category analytics';
      });
  }
});

// ==================== SELECTORS ====================

// Base selector
const selectCategoryState = (state) => state.serviceCategory;

// Category data selectors
export const selectCategories = createSelector(
  selectCategoryState,
  (category) => category.categories
);

export const selectCurrentCategory = createSelector(
  selectCategoryState,
  (category) => category.currentCategory
);

export const selectCategoryTree = createSelector(
  selectCategoryState,
  (category) => category.categoryTree
);

export const selectCategorySelect = createSelector(
  selectCategoryState,
  (category) => category.categorySelect
);

export const selectSubcategories = createSelector(
  selectCategoryState,
  (category) => category.subcategories
);

export const selectSubcategorySelect = createSelector(
  selectCategoryState,
  (category) => category.subcategorySelect
);

export const selectPopularCategories = createSelector(
  selectCategoryState,
  (category) => category.popularCategories
);

export const selectSearchResults = createSelector(
  selectCategoryState,
  (category) => category.searchResults
);

// Statistics selectors
export const selectCategoryAnalytics = createSelector(
  selectCategoryState,
  (category) => category.analytics
);

// Pagination selectors
export const selectCategoryPagination = createSelector(
  selectCategoryState,
  (category) => category.pagination
);

// Loading selectors
export const selectCategoriesLoading = createSelector(
  selectCategoryState,
  (category) => category.loading
);

export const selectCategoryLoading = createSelector(
  selectCategoryState,
  (category) => category.loadingCategory
);

export const selectCategoryTreeLoading = createSelector(
  selectCategoryState,
  (category) => category.loadingTree
);

export const selectCategorySelectLoading = createSelector(
  selectCategoryState,
  (category) => category.loadingSelect
);

export const selectPopularLoading = createSelector(
  selectCategoryState,
  (category) => category.loadingPopular
);

export const selectAnalyticsLoading = createSelector(
  selectCategoryState,
  (category) => category.loadingAnalytics
);

export const selectCategorySubmitting = createSelector(
  selectCategoryState,
  (category) => category.submitting
);

export const selectCategoryUpdating = createSelector(
  selectCategoryState,
  (category) => category.updating
);

// Error selectors
export const selectCategoryError = createSelector(
  selectCategoryState,
  (category) => category.error
);

export const selectSubmitCategoryError = createSelector(
  selectCategoryState,
  (category) => category.submitError
);

export const selectUpdateCategoryError = createSelector(
  selectCategoryState,
  (category) => category.updateError
);

// Success selectors
export const selectSubmitCategorySuccess = createSelector(
  selectCategoryState,
  (category) => category.submitSuccess
);

export const selectUpdateCategorySuccess = createSelector(
  selectCategoryState,
  (category) => category.updateSuccess
);

// UI selectors
export const selectSelectedCategoryId = createSelector(
  selectCategoryState,
  (category) => category.selectedCategoryId
);

export const selectSelectedSubcategoryId = createSelector(
  selectCategoryState,
  (category) => category.selectedSubcategoryId
);

export const selectShowSubcategories = createSelector(
  selectCategoryState,
  (category) => category.showSubcategories
);

export const selectIsEditingCategory = createSelector(
  selectCategoryState,
  (category) => category.isEditing
);

// Derived selectors
export const selectActiveCategories = createSelector(
  selectCategories,
  (categories) => categories?.filter(c => c.isActive) || []
);

export const selectInactiveCategories = createSelector(
  selectCategories,
  (categories) => categories?.filter(c => !c.isActive) || []
);

export const selectFeaturedCategories = createSelector(
  selectCategories,
  (categories) => categories?.filter(c => c.isFeatured) || []
);

export const selectCategoriesWithSubcategories = createSelector(
  selectCategories,
  (categories) => categories?.filter(c => c.subcategories?.length > 0) || []
);

export const selectCategoriesWithoutSubcategories = createSelector(
  selectCategories,
  (categories) => categories?.filter(c => !c.subcategories || c.subcategories.length === 0) || []
);

export const selectTotalCategoryCount = createSelector(
  selectCategoryPagination,
  (pagination) => pagination.total
);

export const selectTotalSubcategoryCount = createSelector(
  selectCategories,
  (categories) => categories?.reduce((sum, c) => sum + (c.subcategories?.length || 0), 0) || 0
);

export const selectTotalProviderCount = createSelector(
  selectCategories,
  (categories) => categories?.reduce((sum, c) => sum + (c.providerCount || 0), 0) || 0
);

export const selectCategoryById = createSelector(
  [selectCategories, (state, id) => id],
  (categories, id) => categories?.find(c => c._id === id) || null
);

export const selectCategoryByName = createSelector(
  [selectCategories, (state, name) => name],
  (categories, name) => categories?.find(c => c.name === name || c.displayName === name) || null
);

// ==================== EXPORTS ====================

export const {
  resetCategoryState,
  clearCategoryErrors,
  clearCategorySuccess,
  setCategoryFilters,
  clearCategoryFilters,
  setCategoryPagination,
  setSelectedCategory,
  setSelectedSubcategory,
  toggleSubcategories,
  setEditingCategory,
  updateLocalCategory,
  addLocalCategory,
  removeLocalCategory,
  updateLocalSubcategory,
  addLocalSubcategory,
  removeLocalSubcategory,
  setCategories,
} = serviceCategorySlice.actions;

export default serviceCategorySlice.reducer;